"use client";

import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type CSSProperties,
} from "react";
import { createPortal } from "react-dom";

type Props = {
	/** Internal neutral URL that replaces the DV entry, e.g. "/" */
	safeInternalPath?: string;
	/** External safe site to redirect to */
	redirectUrl?: string;
	/** Optional classnames to match existing site styling */
	className?: string;
};


const normaliseInternalPath = (path: string) => {
	if (typeof window === "undefined") {
		return path || "/";
	}

	const trimmed = path || "/";

	try {
		const url = new URL(trimmed, window.location.origin);
		const composed = `${url.pathname}${url.search}${url.hash}`;
		return composed || "/";
	} catch {
		return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
	}
};

export default function QuickExitButton({
	safeInternalPath = "/",
	redirectUrl = "https://felixmobile.com.au/plan",
	className = "",
}: Props) {
	const lastEscRef = useRef<number>(0);
	const exitTriggeredRef = useRef(false);
	const [mounted, setMounted] = useState(false);
	const [isStacked, setIsStacked] = useState(false);
	const [isScrolled, setIsScrolled] = useState(false);
	const [baseOffset, setBaseOffset] = useState(120);
	const [navOffset, setNavOffset] = useState(120);

	const quickExit = useCallback(() => {
		if (exitTriggeredRef.current) {
			return;
		}
		exitTriggeredRef.current = true;

		if (typeof window === "undefined") {
			return;
		}

		// Strategy: TPG/iiNet approach
		// 1. Open abc.net.au in a new window/tab (which has no history)
		// 2. Replace DVSF page entry with /plan (history trap page)
		// 3. Inject 8 history entries of /plan so back button clicks stay on /plan
		// 4. Navigate to felixmobile.com.au/plan
		// 5. /plan page has history trap that keeps you on /plan when clicking back
		try {
			// Open abc.net.au in a new window/tab (which has no history)
			const newWindow = window.open("https://www.abc.net.au/", "_blank", "noopener,noreferrer");
			
			if (newWindow) {
				// Step 1: Replace current entry (DVSF page) with /plan to remove it from history
				// This ensures the DV page is completely removed and never appears when clicking back
				// Also update the document title so browser history shows plan page title, not DV form title
				try {
					// Update title before replacing history - browser captures title when history entry is created
					document.title = "SIM only mobile plans with felix | felix mobile";
					window.history.replaceState({ quickExit: true }, "", "/plan");
					// Keep title set - the plan page will update it when it loads
				} catch {
					// If replaceState fails, continue anyway
				}
				
				// Step 2: Push 8 history entries of /plan with unique state objects
				// Unique state objects make each entry distinct so browser won't consolidate them
				// This ensures clicking back 8 times will go through these entries
				try {
					for (let i = 0; i < 8; i++) {
						// Use unique state object to make each entry distinct (URL stays clean /plan)
						window.history.pushState({ quickExit: true, index: i, unique: Date.now() + i }, "", "/plan");
					}
				} catch {
					// If pushState fails, continue anyway
				}
				
				// Step 3: Push final entry with clean /plan URL, then navigate
				// This creates: previous page → /plan (replaced) → /plan (x8) → /plan (final)
				// When user clicks back, they'll go through the 8 entries, all staying on /plan
				try {
					window.history.pushState({ quickExit: true, index: 8, unique: Date.now() + 8 }, "", "/plan");
				} catch {
					// If pushState fails, continue anyway
				}
				
				// Navigate to the plan page
				window.location.href = redirectUrl;
			} else {
				// Popup blocked - fall back to replace method
				// Still inject history entries for protection and remove DV page
				try {
					// Replace DV page with /plan and update title
					document.title = "SIM only mobile plans with felix | felix mobile";
					window.history.replaceState({ quickExit: true }, "", "/plan");
					// Push 8 entries with unique state objects
					for (let i = 0; i < 8; i++) {
						window.history.pushState({ quickExit: true, index: i, unique: Date.now() + i }, "", "/plan");
					}
					// Push final clean entry
					window.history.pushState({ quickExit: true, index: 8, unique: Date.now() + 8 }, "", "/plan");
				} catch {
					// If history manipulation fails, continue anyway
				}
				window.location.href = redirectUrl;
			}
		} catch {
			// Fallback if anything fails
			try {
				// Still try to inject history entries for protection and remove DV page
				document.title = "SIM only mobile plans with felix | felix mobile";
				window.history.replaceState({ quickExit: true }, "", "/plan");
				for (let i = 0; i < 8; i++) {
					window.history.pushState({ quickExit: true, index: i, unique: Date.now() + i }, "", "/plan");
				}
				window.history.pushState({ quickExit: true, index: 8, unique: Date.now() + 8 }, "", "/plan");
				window.location.href = redirectUrl;
			} catch {
				// Final fallback - just redirect (DV page will still be in history, but better than nothing)
				window.location.href = redirectUrl;
			}
		}
	}, [redirectUrl, safeInternalPath]);

	useEffect(() => {
		setMounted(true);
		return () => setMounted(false);
	}, []);

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				const now = Date.now();
				if (now - lastEscRef.current < 800) {
					quickExit();
				}
				lastEscRef.current = now;
			}
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [quickExit]);

	useEffect(() => {
		if (typeof window === "undefined") return;

		const updateLayout = () => {
			setIsStacked(window.innerWidth < 992);
		};

		updateLayout();
		window.addEventListener("resize", updateLayout);
		return () => window.removeEventListener("resize", updateLayout);
	}, []);

	useEffect(() => {
		if (typeof window === "undefined") return;

		const handleScroll = () => {
			setIsScrolled(window.scrollY > 0);
		};

		handleScroll();
		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	useEffect(() => {
		if (typeof window === "undefined") return;

		const calculateOffset = () => {
			const navEl = document.querySelector<HTMLElement>('[role="navigation"]');
			if (!navEl) {
				setBaseOffset(120);
				return;
			}

			const rectHeight = navEl.getBoundingClientRect().height;
			if (rectHeight > 0) {
				setBaseOffset(Math.max(rectHeight + 14, 120));
				return;
			}

			const computed = window.getComputedStyle(navEl);
			const paddingTop = Number.parseFloat(computed.paddingTop || "0");
			const paddingBottom = Number.parseFloat(computed.paddingBottom || "0");
			const total = navEl.offsetHeight + paddingTop + paddingBottom;
			setBaseOffset(Math.max(total, 120));
		};

		calculateOffset();
		window.addEventListener("resize", calculateOffset);
		return () => window.removeEventListener("resize", calculateOffset);
	}, []);

	useEffect(() => {
		if (isScrolled) {
			setNavOffset(70);
			return;
		}

		setNavOffset(150);
	}, [isScrolled]);

	const containerClasses = useMemo(
		() =>
			[
				"quick-exit-container",
				isStacked ? "quick-exit-stacked" : "quick-exit-inline",
				className,
			]
				.filter(Boolean)
				.join(" "),
		[className, isStacked],
	);

	const messageClasses = useMemo(
		() =>
			[
				"quick-exit-message",
				isStacked ? "quick-exit-message-stacked" : "quick-exit-message-inline",
			].join(" "),
		[isStacked],
	);

	const buttonClasses = useMemo(
		() =>
			[
				"quick-exit-button",
				"inline-flex items-center justify-center rounded-full border border-white px-4 py-2 text-sm font-medium text-white transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
			].join(" "),
		[],
	);

	const containerStyle: CSSProperties = {
		position: "fixed",
		top: navOffset,
		left: 0,
		right: 0,
		zIndex: 90,
		display: "flex",
		flexDirection: isStacked ? "column" : "row",
		justifyContent: "center",
		alignItems: "center",
		padding: "16px 24px",
		backgroundColor: "var(--color-watermelon, #FE5751)",
		cursor: "pointer",
	};

	const messageStyle: CSSProperties = {
		color: "#fff",
		textAlign: "center",
		marginBottom: isStacked ? 12 : 0,
		marginRight: isStacked ? 0 : 450,
		fontSize: "0.95rem",
		fontWeight: 500,
	};

	const buttonStyle: CSSProperties = {
		color: "#fff",
		marginLeft: 0,
		width: isStacked ? "fit-content" : "auto",
		alignSelf: isStacked ? "center" : "auto",
	};

	if (!mounted) {
		return null;
	}

	return createPortal(
		// biome-ignore lint/a11y/useKeyWithClickEvents: The button inside handles keyboard events
		<div
			className={containerClasses}
			style={containerStyle}
			onClick={quickExit}
			title="Quick exit to a safe site"
		>
			<span className={messageClasses} style={messageStyle}>
				Quickly exit this page and go to felixmobile.com.au/plan
			</span>
			<button
				type="button"
				onClick={quickExit}
				aria-label="Quick exit to a safe site"
				title="Quick exit to a safe site"
				className={buttonClasses}
				style={buttonStyle}
			>
				Quick exit
			</button>
		</div>,
		document.body,
	);
}
